import { createWebHistory, createRouter } from "vue-router";
import pinia, { useAuthStore } from "@/state/pinia";
import { useProjectStore } from "@/state/pinia/project";
import routes from "./routes";
import appConfig from "../app.config.json";

let bootChecked = false;
let mustCreateWorkspace = false;
let lastTokenCheck = null;
const TOKEN_CHECK_INTERVAL = 60000; // Check token max once per minute

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: "history",
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});

// Before each route evaluates...
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore(pinia);

  if (!auth.token && auth.loadToken) {
    await auth.loadToken();
  }

  const publicPrefixes = [
    "/login",
    "/logout",
    "/auth/callback",
    "/workspaces/new"
  ];
  const isPublic = publicPrefixes.some(p => to.path.startsWith(p));
  const token = auth.getToken && auth.getToken();

  if (token && from.path === "/workspaces/new" && to.path !== "/workspaces/new") {
    bootChecked = false;
    mustCreateWorkspace = false;
  }

  if (!isPublic && !token) {
    const ssoUrl = process.env.VUE_APP_SSO_URL;
    const ssoClientId = process.env.VUE_APP_SSO_CLIENT_ID;
    if (!ssoUrl || !ssoClientId) {
      // No SSO - use local login
      return next("/login");
    }
    auth.redirectToSso();
    return;
  }

  // Check token validity: on initial page load, first boot, after interval, or when navigating from public routes
  const now = Date.now();
  const isInitialLoad = from.path === "/" && from.name === null; // User opened page or refreshed
  const shouldCheckToken = token && (
    isInitialLoad ||
    !bootChecked ||
    !lastTokenCheck ||
    (now - lastTokenCheck) > TOKEN_CHECK_INTERVAL ||
    (isPublic !== publicPrefixes.some(p => from.path.startsWith(p))) // transitioning from/to public routes
  );

  if (shouldCheckToken) {
    try {
      const base = process.env.VUE_APP_BE_URL || import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${base}api/me/status`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
      });
      lastTokenCheck = now;

      if (res.ok) {
        const data = await res.json();
        mustCreateWorkspace = !!data?.must_create_workspace;
        bootChecked = true;
      } else if (res.status === 401) {
        // Token is invalid or expired, logout and redirect to login/SSO
        auth.logout();
        bootChecked = false;
        mustCreateWorkspace = false;
        lastTokenCheck = null;
        auth.redirectToSso();
        return;
      } else if (res.status === 428) {
        // gdyby middleware od razu zablokował
        mustCreateWorkspace = true;
        bootChecked = true;
      }
    } catch (_) {
      // w razie błędu zostaw false — backend i tak wymusi 428
      lastTokenCheck = now;
      bootChecked = true;
    }
  }

  // Redirect account settings to SSO when SSO is enabled
  if (to.path === '/user/settings' && process.env.VUE_APP_SSO_URL) {
    const ssoBase = process.env.VUE_APP_SSO_URL.replace(/\/+$/, '');
    window.location.href = `${ssoBase}/account/settings?from=cms`;
    return;
  }

  if (token && mustCreateWorkspace && to.path !== "/workspaces/new") {
    return next("/workspaces/new");
  }

  // Auto-redirect from root based on user access
  if (token && to.path === "/" && to.name === "default") {
    const user = auth.getUser?.();
    const userUuid = user?.uuid || 'default';
    const lastProject = localStorage.getItem(`activeProject_${userUuid}`);
    const projects = user?.projects || {};
    const projectCodes = Object.keys(projects);
    const workspaces = user?.workspaces || {};
    const hasWorkspaces = Object.keys(workspaces).length > 0;

    if (lastProject) {
      return next(`/project/${lastProject}/dashboard`);
    } else if (projectCodes.length === 1) {
      return next(`/project/${projectCodes[0]}/dashboard`);
    } else if (hasWorkspaces) {
      return next("/projects");
    } else if (projectCodes.length > 0) {
      return next(`/project/${projectCodes[0]}/dashboard`);
    }
  }

  // Sync project from URL to store
  const projectCode = to.params.project_code;
  if (token && projectCode) {
    const projectStore = useProjectStore(pinia);
    if (projectStore.activeProject !== projectCode) {
      projectStore.setActive(projectCode);
    }
  }

  // Block workspace-admin-only routes for non-admins
  if (token && to.meta.requiresWorkspaceAdmin) {
    if (!auth.isAdminOfActiveWorkspace?.()) {
      return next("/projects");
    }
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            console.log("args" + args);
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  document.title = routeTo.meta.title + " | " + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
